import React, { useMemo, useState, useEffect } from "react";
import {
  CheckCircle2,
  AlertTriangle,
  UserMinus,
  Loader2,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ScrollArea } from "@/components/ui/scroll-area";
import employeesCanonical from "@/api/employees-canonical";
import { formatApiError } from "@/api/client";

/**
 * Phase 10.7 — Post-TE350 upload activity review.
 *
 * Consumes the `activity_review` block returned by `/api/excel/ingest`
 * when a TE350 report lands. Presents two zones:
 *
 *   1) Green banner (informational) — canonicals that were previously
 *      inactive because of the `no_activity_te350_*` reason but have
 *      re-appeared in the freshly-ingested file. They were reactivated
 *      by the backend already; no user action needed.
 *
 *   2) Yellow section (actionable) — active canonicals with TE350
 *      aliases that DID NOT show up in the file. Rendered as a table
 *      with pre-checked checkboxes so the admin can confirm the bulk
 *      deactivation in one click.
 *
 * The dialog only renders when at least one of the two zones has data,
 * mirroring the SPA-level gate in `Upload.jsx`.
 */
export default function TE350ActivityReviewDialog({
  open,
  onOpenChange,
  activityReview,
  uploadId,
  filename,
  onApplied,
}) {
  const to_deactivate = useMemo(
    () => activityReview?.to_deactivate_candidates ?? [],
    [activityReview],
  );
  const auto_reactivated = useMemo(
    () => activityReview?.auto_reactivated ?? [],
    [activityReview],
  );

  // Pre-select every candidate — the admin can uncheck the false positives.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedIds(new Set(to_deactivate.map((c) => c.canonical_id)));
    }
  }, [open, to_deactivate]);

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = (checked) => {
    setSelectedIds(
      checked ? new Set(to_deactivate.map((c) => c.canonical_id)) : new Set(),
    );
  };

  const doDeactivate = async () => {
    if (selectedIds.size === 0) {
      toast.info("No hay empleados seleccionados para desactivar.");
      return;
    }
    setLoading(true);
    // Reason string is round-trippable — future ingests match it via
    // the `no_activity_te350_` prefix to auto-reactivate.
    const reason = `no_activity_te350_${uploadId || "unknown"}_${new Date()
      .toISOString()
      .slice(0, 19)
      .replace(/[:-]/g, "")}`;
    try {
      const { data } = await employeesCanonical.bulkDeactivate({
        canonical_ids: Array.from(selectedIds),
        reason,
        source_file_id: uploadId,
      });
      const parts = [];
      if (data.deactivated > 0) parts.push(`${data.deactivated} desactivados`);
      if (data.skipped > 0) parts.push(`${data.skipped} ya inactivos`);
      if (auto_reactivated.length > 0)
        parts.push(`${auto_reactivated.length} reactivados`);
      toast.success(parts.join(" · ") || "Sin cambios.");
      if (data.errors?.length) {
        console.warn("bulk-deactivate errors:", data.errors);
        toast.warning(`${data.errors.length} error(es) — revisa consola.`);
      }
      onApplied?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const ignoreAll = () => {
    if (auto_reactivated.length > 0) {
      toast.success(
        `${auto_reactivated.length} empleado(s) reactivados automáticamente.`,
      );
    }
    onOpenChange(false);
  };

  const allChecked =
    to_deactivate.length > 0 && selectedIds.size === to_deactivate.length;
  const someChecked = selectedIds.size > 0 && !allChecked;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl"
        data-testid="te350-activity-review-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserMinus className="h-4 w-4" />
            Revisión de actividad TE350
          </DialogTitle>
          <DialogDescription>
            Al procesar{" "}
            <span className="font-mono text-foreground">
              {filename || "el archivo"}
            </span>{" "}
            detectamos empleados con actividad pendiente de revisión.
          </DialogDescription>
        </DialogHeader>

        {auto_reactivated.length > 0 && (
          <Alert
            data-testid="auto-reactivated-section"
            className="border-emerald-500/40 bg-emerald-500/5"
          >
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            <AlertTitle className="text-emerald-400">
              Reactivados automáticamente ({auto_reactivated.length})
            </AlertTitle>
            <AlertDescription className="text-xs">
              Estos empleados estaban desactivados por falta de actividad y
              volvieron a aparecer en este TE350. Ya fueron reactivados
              automáticamente:
              <div className="mt-2 flex flex-wrap gap-1.5">
                {auto_reactivated.map((c) => (
                  <Badge
                    key={c.canonical_id}
                    variant="outline"
                    data-testid={`reactivated-${c.canonical_id}`}
                    className="border-emerald-500/40 text-emerald-300"
                  >
                    {c.canonical_name}
                  </Badge>
                ))}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {to_deactivate.length > 0 && (
          <div
            data-testid="to-deactivate-section"
            className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3"
          >
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-400" />
                <span className="text-sm font-medium text-amber-300">
                  Empleados sin actividad en este TE350 ({to_deactivate.length})
                </span>
              </div>
              <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
                <Checkbox
                  checked={allChecked}
                  data-testid="deactivate-select-all"
                  onCheckedChange={toggleAll}
                  aria-label="Seleccionar todos"
                  className={someChecked ? "data-[state=checked]:bg-amber-500" : ""}
                />
                Seleccionar todos
              </label>
            </div>
            <ScrollArea className="max-h-72 pr-2">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-background/95 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="w-8 py-1.5"></th>
                    <th className="py-1.5 text-left">Nombre</th>
                    <th className="py-1.5 text-left">Aliases TE350</th>
                    <th className="py-1.5 text-left">Última actividad</th>
                  </tr>
                </thead>
                <tbody>
                  {to_deactivate.map((c) => (
                    <tr
                      key={c.canonical_id}
                      data-testid={`deactivate-row-${c.canonical_id}`}
                      className="border-t border-border/50 hover:bg-accent/30"
                    >
                      <td className="py-1.5">
                        <Checkbox
                          checked={selectedIds.has(c.canonical_id)}
                          onCheckedChange={() => toggle(c.canonical_id)}
                          data-testid={`deactivate-checkbox-${c.canonical_id}`}
                        />
                      </td>
                      <td className="py-1.5 font-medium">
                        {c.canonical_name}
                      </td>
                      <td className="py-1.5">
                        <div className="flex flex-wrap gap-1">
                          {(c.te350_aliases || []).map((code) => (
                            <Badge
                              key={code}
                              variant="secondary"
                              className="font-mono text-xs"
                            >
                              {code}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td className="py-1.5 text-xs text-muted-foreground">
                        {(c.last_seen_at || "").slice(0, 10) || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollArea>
            <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Info className="h-3 w-3" />
              Podrán reactivarse automáticamente si vuelven a aparecer en un
              TE350 futuro.
            </div>
          </div>
        )}

        {to_deactivate.length === 0 && auto_reactivated.length === 0 && (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Sin cambios detectados. Todos los empleados con alias TE350
            aparecen en el archivo.
          </p>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={ignoreAll}
            disabled={loading}
            data-testid="ignore-all-btn"
          >
            Ignorar todos
          </Button>
          {to_deactivate.length > 0 && (
            <Button
              onClick={doDeactivate}
              disabled={loading || selectedIds.size === 0}
              data-testid="bulk-deactivate-confirm-btn"
              variant="destructive"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  Desactivando…
                </>
              ) : (
                <>
                  <UserMinus className="mr-1.5 h-3.5 w-3.5" />
                  Desactivar seleccionados ({selectedIds.size})
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
